/**
 * Location Configuration for Builder Buddy
 *
 * This file defines the available provinces and cities for the app.
 * Currently only Alberta/Calgary is fully supported - other locations
 * are marked as "coming soon" (work in progress).
 */

export interface City {
  id: string;
  name: string;
  isSupported: boolean;
  landUseBylaw?: string;
  permitPortalUrl?: string;
  contactPhone?: string;
  planningUrl?: string;
  firePermitsUrl?: string;
  roadsUrl?: string;
}

export interface Province {
  id: string;
  name: string;
  abbreviation: string;
  isSupported: boolean;
  buildingCode: string;
  buildingCodeVersion: string;
  safetyCodesPortal?: string;
  cities: City[];
}

export const PROVINCES: Province[] = [
  {
    id: "ab",
    name: "Alberta",
    abbreviation: "AB",
    isSupported: true,
    buildingCode: "NBC(AE)",
    buildingCodeVersion: "2023",
    safetyCodesPortal: "https://www.safetycodes.ab.ca/",
    cities: [
      {
        id: "calgary",
        name: "Calgary",
        isSupported: true,
        landUseBylaw: "Land Use Bylaw 1P2007",
        permitPortalUrl: "https://apply.calgary.ca/",
        contactPhone: "311",
        planningUrl: "https://www.calgary.ca/planning",
        firePermitsUrl: "https://www.calgary.ca/safety/fire-permits.html",
        roadsUrl: "https://www.calgary.ca/roads/driveway-crossing.html",
      },
      {
        id: "edmonton",
        name: "Edmonton",
        isSupported: false, // Coming soon
        landUseBylaw: "Zoning Bylaw 12800",
        permitPortalUrl: "https://www.edmonton.ca/permits",
        contactPhone: "311",
        planningUrl:
          "https://www.edmonton.ca/city_government/urban_planning_and_design",
      },
      {
        id: "red-deer",
        name: "Red Deer",
        isSupported: false,
      },
      {
        id: "lethbridge",
        name: "Lethbridge",
        isSupported: false,
      },
    ],
  },
  {
    id: "bc",
    name: "British Columbia",
    abbreviation: "BC",
    isSupported: false,
    buildingCode: "BC Building Code",
    buildingCodeVersion: "2024",
    cities: [
      { id: "vancouver", name: "Vancouver", isSupported: false },
      { id: "victoria", name: "Victoria", isSupported: false },
      { id: "surrey", name: "Surrey", isSupported: false },
    ],
  },
  {
    id: "on",
    name: "Ontario",
    abbreviation: "ON",
    isSupported: false,
    buildingCode: "Ontario Building Code",
    buildingCodeVersion: "2024",
    cities: [
      { id: "toronto", name: "Toronto", isSupported: false },
      { id: "ottawa", name: "Ottawa", isSupported: false },
      { id: "mississauga", name: "Mississauga", isSupported: false },
    ],
  },
  {
    id: "qc",
    name: "Quebec",
    abbreviation: "QC",
    isSupported: false,
    buildingCode: "Code de construction du Québec",
    buildingCodeVersion: "2023",
    cities: [
      { id: "montreal", name: "Montreal", isSupported: false },
      { id: "quebec-city", name: "Quebec City", isSupported: false },
    ],
  },
  {
    id: "sk",
    name: "Saskatchewan",
    abbreviation: "SK",
    isSupported: false,
    buildingCode: "National Building Code (Saskatchewan)",
    buildingCodeVersion: "2020",
    cities: [
      { id: "saskatoon", name: "Saskatoon", isSupported: false },
      { id: "regina", name: "Regina", isSupported: false },
    ],
  },
  {
    id: "mb",
    name: "Manitoba",
    abbreviation: "MB",
    isSupported: false,
    buildingCode: "Manitoba Building Code",
    buildingCodeVersion: "2020",
    cities: [{ id: "winnipeg", name: "Winnipeg", isSupported: false }],
  },
  {
    id: "ns",
    name: "Nova Scotia",
    abbreviation: "NS",
    isSupported: false,
    buildingCode: "Nova Scotia Building Code",
    buildingCodeVersion: "2022",
    cities: [{ id: "halifax", name: "Halifax", isSupported: false }],
  },
  {
    id: "nb",
    name: "New Brunswick",
    abbreviation: "NB",
    isSupported: false,
    buildingCode: "National Building Code (New Brunswick)",
    buildingCodeVersion: "2020",
    cities: [
      { id: "saint-john", name: "Saint John", isSupported: false },
      { id: "moncton", name: "Moncton", isSupported: false },
    ],
  },
  {
    id: "nl",
    name: "Newfoundland and Labrador",
    abbreviation: "NL",
    isSupported: false,
    buildingCode: "National Building Code (NL)",
    buildingCodeVersion: "2020",
    cities: [{ id: "st-johns", name: "St. John's", isSupported: false }],
  },
  {
    id: "pe",
    name: "Prince Edward Island",
    abbreviation: "PE",
    isSupported: false,
    buildingCode: "National Building Code (PEI)",
    buildingCodeVersion: "2020",
    cities: [
      { id: "charlottetown", name: "Charlottetown", isSupported: false },
    ],
  },
];

// Helper functions
export function getProvince(provinceId: string): Province | undefined {
  return PROVINCES.find((p) => p.id === provinceId);
}

export function getCity(provinceId: string, cityId: string): City | undefined {
  const province = getProvince(provinceId);
  return province?.cities.find((c) => c.id === cityId);
}

export function getSupportedProvinces(): Province[] {
  return PROVINCES.filter((p) => p.isSupported);
}

export function getSupportedCities(provinceId: string): City[] {
  const province = getProvince(provinceId);
  return province?.cities.filter((c) => c.isSupported) || [];
}

export function isLocationSupported(
  provinceId: string,
  cityId?: string,
): boolean {
  const province = getProvince(provinceId);
  if (!province?.isSupported) return false;
  if (cityId) {
    const city = getCity(provinceId, cityId);
    return city?.isSupported || false;
  }
  return true;
}

// Default location
export const DEFAULT_PROVINCE = "ab";
export const DEFAULT_CITY = "calgary";
